/**
 * Mikasa Omni Token Requests
 * Some mikasa endpoints require an `omniToken` for authentication.
 * We don't expect many requests of this type, so:
 * - we don't create a generic client for them
 * - we keep them in a single file.
 * - we don't use react-query for these requests
 * - we don't use custom hooks for these requests
 */

import * as Sentry from '@sentry/browser'

import { convertObjectKeysToCamelCase } from '../../util/convert_object_keys_to_camel_case'
import { MikasaCustomerDisplay } from './type/mikasa_customer_display'

const mikasaApiUrl = process.env.REACT_APP_MIKASA_URI

/**
 * Fetches all customers (Loan Officers and Real Estate Agents) visible to the current user,
 * using an omniToken for authentication.
 *
 * This is a low-level request to the Mikasa API that does not use react-query or custom hooks.
 *
 * @throws Never throws, but logs errors to Sentry and returns `null` if the request fails.
 */
export async function getAllCustomers(omniToken: string): Promise<MikasaCustomerDisplay[] | null> {
  try {
    if (!mikasaApiUrl) {
      throw new Error('REACT_APP_MIKASA_URI is not defined')
    }

    // Not all code is in TypeScript, so we need to check the type of omniToken
    if (typeof omniToken !== 'string' || !omniToken.length) {
      throw new Error('Omni token is required to fetch customers')
    }

    const response = await fetch(`${mikasaApiUrl}/native/customer-profiles`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${omniToken}`
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch customers: ${response.statusText}`)
    }

    const data = await response.json()

    return convertObjectKeysToCamelCase(data)
  } catch (error) {
    Sentry.captureException(new Error('getAllCustomersFailed'), { extra: { error } })

    return null
  }
}
